import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { WebService } from '../web/web.service';
import { Portal, PortalSesion } from './portal.model';

/** Fuente única de la lista de portales del super admin. */
@Injectable({ providedIn: 'root' })
export class PortalsService {
  private readonly cacheKey = 'allSessions';
  private cache: Portal[] | null = null;

  constructor(private webService: WebService) {}

  getPortals$(forceRefresh = false): Observable<Portal[]> {
    if (!forceRefresh) {
      const cached = this.readCache();
      if (cached !== null) return of(cached);
    }

    return this.webService.get(`${this.webService.HOST}/auth/portals`).pipe(
      map((response: unknown) => this.normalize(response)),
      tap((portals) => this.writeCache(portals))
    );
  }

  refresh(): void {
    this.cache = null;
    sessionStorage.removeItem(this.cacheKey);
    localStorage.removeItem(this.cacheKey);
  }

  private normalize(response: unknown): Portal[] {
    if (!Array.isArray(response)) return [];
    return response
      .filter((portal): portal is Portal => this.isPortal(portal))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private isPortal(value: unknown): value is Portal {
    if (!this.isRecord(value)) return false;
    return typeof value['id'] === 'number'
      && typeof value['name'] === 'string'
      && this.isPortalSesion(value['sesion']);
  }

  private isPortalSesion(value: unknown): value is PortalSesion {
    if (!this.isRecord(value)) return false;
    return (typeof value['user_id'] === 'number' || value['user_id'] === null)
      && (typeof value['customer'] === 'number' || value['customer'] === null)
      && typeof value['partner'] === 'number'
      && Array.isArray(value['config'])
      && value['config'].every((configId) => typeof configId === 'number');
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private readCache(): Portal[] | null {
    if (this.cache !== null) return this.cache;
    const raw = sessionStorage.getItem(this.cacheKey);
    if (!raw) return null;

    try {
      this.cache = this.normalize(JSON.parse(raw) as unknown);
      return this.cache;
    } catch {
      sessionStorage.removeItem(this.cacheKey);
      return null;
    }
  }

  private writeCache(portals: Portal[]): void {
    this.cache = portals;
    sessionStorage.setItem(this.cacheKey, JSON.stringify(portals));
    localStorage.removeItem(this.cacheKey);
  }
}
